import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import AddToCollection from "./InfiniteRadio/AddToCollection";
import FeedbackForm from "./InfiniteRadio/FeedbackForm";
import VoteButton from "./Labs/VoteButton";
import makeTracker from "./InfiniteRadio/track";
import Lyrics from "./InfiniteRadio/Lyrics";
import Visualizer from "./InfiniteRadio/Visualizer";
import { activeLineIndex, dayKey, nowPlaying, playlistFor } from "./InfiniteRadio/station";

// InfiniteRadio - original songs written entirely at one CEFR level, playing on
// a shared clock.
//
// Proof of concept. The whole station arrives as props from a JSON manifest
// built offline by `rake infinite_radio:generate`; there is no backend here and
// nothing is recorded about what anyone listened to.
//
// Unrelated to Clozemaster Radio, the sentence-playback feature in the apps.

// The lyric view only needs to be right to about a syllable, and re-rendering
// on every animation frame costs far more than it buys. The canvas reads the
// clock directly and does run at frame rate.
const POSITION_INTERVAL_MS = 60;

const MODES = [
  { key: "read", label: "Lyrics" },
  { key: "cloze", label: "Cloze" },
  { key: "off", label: "Off" },
];

// How far behind the broadcast you have to be before the live button lights up.
// Small seeks and ordinary drift shouldn't make it flicker.
const BEHIND_LIVE_MS = 2500;

function readStored(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    // Private windows and blocked site data both throw here.
    return null;
  }
}

// "both" was a mode of its own before English was split into a toggle. Anyone
// carrying it wanted lyrics plus the translation, so it maps to exactly that
// across the two settings rather than quietly resetting their preference.
// Cloze with the translation, by default. Cloze is what makes this a practice
// page rather than a lyrics video, so a listener who never touches a control
// should still get it - and having the English alongside is what keeps a hidden
// word guessable from context instead of a dead end. An explicit stored choice
// wins over both defaults.
function readStoredMode() {
  const stored = readStored("ir-mode");
  if (stored === "both") return "read";

  return MODES.some((mode) => mode.key === stored) ? stored : "cloze";
}

function readStoredEnglish() {
  const stored = readStored("ir-english");
  if (stored === "1") return true;
  if (stored === "0") return false;

  // Nothing stored yet. That also covers the "both" carry-over from before
  // English was split into its own toggle, which meant exactly this.
  return true;
}

function formatTime(ms) {
  const total = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

// Drawn rather than imported: two glyphs is not worth an icon dependency, and
// inline SVG inherits currentColor so the button only has to state one colour.
function PlayIcon() {
  return (
    <svg aria-hidden="true" className="ir-play-icon ir-play-icon-play" fill="currentColor" viewBox="0 0 24 24">
      <path d="M7.5 4.6v14.8a1 1 0 0 0 1.53.85l11.6-7.4a1 1 0 0 0 0-1.7L9.03 3.75a1 1 0 0 0-1.53.85Z" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg aria-hidden="true" className="ir-play-icon" fill="currentColor" viewBox="0 0 24 24">
      <path d="M7.9 4h1.4a1.9 1.9 0 0 1 1.9 1.9v12.2a1.9 1.9 0 0 1-1.9 1.9H7.9A1.9 1.9 0 0 1 6 18.1V5.9A1.9 1.9 0 0 1 7.9 4Z" />
      <path d="M14.7 4h1.4A1.9 1.9 0 0 1 18 5.9v12.2a1.9 1.9 0 0 1-1.9 1.9h-1.4a1.9 1.9 0 0 1-1.9-1.9V5.9A1.9 1.9 0 0 1 14.7 4Z" />
    </svg>
  );
}

// Cover art is the one step allowed to fail without losing the track, so a
// track may have none. The palette always survives, and two of its colours make
// a backdrop good enough that a missing cover reads as a design choice.
function backdropStyle(track) {
  if (track.artUrl) return { backgroundImage: `url(${track.artUrl})` };

  const [from, to] = track.palette && track.palette.length > 1 ? track.palette : ["#2b3a67", "#b34a6e"];
  return { backgroundImage: `linear-gradient(140deg, ${from}, ${to})` };
}

export default function InfiniteRadio({
  collectionsUrl, defaultCollectionName, feedbackUrl, isPro, labsUrl, proUrl, signInUrl, signedIn,
  station, stations, tracks, vote,
}) {
  const audioRef = useRef(null);
  const [mode, setMode] = useState(readStoredMode);
  const [showEnglish, setShowEnglish] = useState(readStoredEnglish);
  const [playing, setPlaying] = useState(false);
  // Where the broadcast is, as opposed to where this listener is. The two differ
  // whenever someone scrubs back, which is the whole point of having both.
  const [live, setLive] = useState({ index: 0, offsetMs: 0 });
  // The line whose add-to-collection form is open, and whether the music was
  // running when it opened - closing has to put playback back how it found it.
  const [adding, setAdding] = useState(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const track_ = useMemo(() => makeTracker({
    cefrLevel: station.cefrLevel,
    isPro,
    language: station.language,
    signedIn,
  }), [isPro, signedIn, station]);

  // Fires once per visit, and is the denominator for everything else.
  useEffect(() => {
    // Empty deps on purpose: this counts visits, not renders, so it must not
    // re-fire when the deep link or the tracker identity resolves.
    track_("viewed", { deep_linked: deepLinkIndex >= 0 });
  }, []);
  const resumeAfterModal = useRef(false);
  // Autoplay with sound is blocked everywhere, so the station starts paused and
  // the first click both starts audio and seeks to wherever the clock already is.
  const [tunedIn, setTunedIn] = useState(false);
  const [positionMs, setPositionMs] = useState(0);
  const [index, setIndex] = useState(0);

  // Reshuffled once a day, identically on every client, from the date alone.
  const playlist = useMemo(() => playlistFor(tracks, dayKey(new Date())), [tracks]);
  const track = playlist[index];

  // ?track=<id> opens on one song instead of joining the broadcast, so a link in
  // a comment lands on the song it was about. Read once: a later history change
  // shouldn't pull the listener off whatever is playing.
  const deepLinkIndex = useMemo(() => {
    const wanted = new URLSearchParams(window.location.search).get("track");
    return wanted ? playlist.findIndex((item) => item.id === wanted) : -1;
  }, [playlist]);

  useEffect(() => {
    if (deepLinkIndex >= 0) setIndex(deepLinkIndex);
  }, [deepLinkIndex]);

  useEffect(() => {
    try {
      window.localStorage.setItem("ir-mode", mode);
      window.localStorage.setItem("ir-english", showEnglish ? "1" : "0");
    } catch (e) {
      // Not worth failing a render over.
    }
  }, [mode, showEnglish]);

  // Where the station is, independent of whether anyone is listening.
  const syncToClock = useCallback(() => {
    const current = nowPlaying(playlist, new Date());
    if (!current) return;

    setIndex(current.index);
    if (audioRef.current) audioRef.current.currentTime = current.offsetMs / 1000;
  }, [playlist]);

  const tuneIn = useCallback(() => {
    // A deep link means "play this song", so it starts at the top rather than
    // dropping into the middle of it. Everything after it is live as usual.
    if (deepLinkIndex >= 0) {
      setIndex(deepLinkIndex);
    } else {
      syncToClock();
    }

    setTunedIn(true);
    track_("tuned_in", { deep_linked: deepLinkIndex >= 0 });
    if (audioRef.current) audioRef.current.play().catch(() => setPlaying(false));
  }, [deepLinkIndex, syncToClock, track_]);

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (audio.paused) {
      // Rejoining after a pause means rejoining the broadcast, not resuming
      // where it was left - that's the difference from a music player.
      syncToClock();
      audio.play().catch(() => setPlaying(false));
    } else {
      audio.pause();
    }
  }, [syncToClock]);

  // One timer for the lyric clock. The canvas gets a getter instead, so it can
  // read the true position every frame without going through React.
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (audioRef.current) setPositionMs(audioRef.current.currentTime * 1000);

      const current = nowPlaying(playlist, new Date());
      if (current) setLive({ index: current.index, offsetMs: current.offsetMs });
    }, POSITION_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [playlist]);

  // Scrubbing within the song you're already hearing. Seeking past the live edge
  // is clamped rather than blocked - you can catch up to the broadcast, but you
  // can't hear a bar of it before it airs.
  const seek = useCallback((event) => {
    const audio = audioRef.current;
    if (!audio) return;

    const wanted = Number(event.target.value);
    const ceiling = live.index === index ? live.offsetMs : audio.duration * 1000 || wanted;
    audio.currentTime = Math.min(wanted, ceiling) / 1000;
    setPositionMs(audio.currentTime * 1000);
  }, [index, live]);

  // Editing a lyric while it's being sung is hopeless, so opening the form
  // pauses. Closing only resumes if this is what paused it - a listener who had
  // already hit pause should stay paused.
  const openAdd = useCallback((line) => {
    const audio = audioRef.current;
    resumeAfterModal.current = Boolean(audio && !audio.paused);
    if (audio && !audio.paused) audio.pause();
    setAdding(line);
    // The one place a visitor meets the Pro wall, so this is the event that says
    // how many of them did.
    track_("save_opened");
  }, [track_]);

  const closeAdd = useCallback(() => {
    setAdding(null);
    if (resumeAfterModal.current && audioRef.current) {
      audioRef.current.play().catch(() => setPlaying(false));
    }
    resumeAfterModal.current = false;
  }, []);

  // Replay one line. Tuning in first if necessary, since the obvious reading of
  // "play from here" is that something starts playing.
  const playFrom = useCallback((line) => {
    const audio = audioRef.current;
    if (!audio) return;

    track_("play_from_line");
    audio.currentTime = line.startMs / 1000;
    setPositionMs(line.startMs);
    setTunedIn(true);
    if (audio.paused) audio.play().catch(() => setPlaying(false));
  }, [track_]);

  // Explicit rather than rewriting the address bar as tracks change: sharing
  // should capture what you meant to share, not whatever happened to be on.
  const share = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("track", track.id);

    const done = () => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    };

    if (navigator.clipboard) {
      navigator.clipboard.writeText(url.toString()).then(done).catch(() => {});
      track_("shared", { track_id: track.id });
    }
  }, [track, track_]);

  const getPositionMs = useCallback(() => (audioRef.current ? audioRef.current.currentTime * 1000 : 0), []);

  // Advance off the station clock rather than index + 1: a tab that was
  // throttled or asleep rejoins where the broadcast actually is.
  const onEnded = useCallback(() => {
    const current = nowPlaying(playlist, new Date());
    setIndex(current ? current.index : (index + 1) % playlist.length);
    setPositionMs(0);
  }, [index, playlist]);

  // A new src needs an explicit play() - changing it stops the element.
  useEffect(() => {
    if (tunedIn && audioRef.current) audioRef.current.play().catch(() => setPlaying(false));
  }, [index, tunedIn]);

  if (!track) return <p className="ir-empty">No tracks generated yet.</p>;

  const lines = track.lines || [];
  const active = activeLineIndex(lines, positionMs);
  const upNext = playlist[(index + 1) % playlist.length];

  // Matched on the station rather than the current path, which may carry a
  // ?track= the options don't have.
  const currentStationUrl = (stations.find((option) => (
    option.languageCode === station.languageCode && option.cefrLevel === station.cefrLevel
  )) || {}).url || "";

  // Behind live means either scrubbed back within this song, or still playing a
  // song the broadcast has already moved past.
  const onLiveTrack = live.index === index;
  const behindLive = tunedIn && (!onLiveTrack || live.offsetMs - positionMs > BEHIND_LIVE_MS);
  // Only the part of the song that has actually aired is seekable.
  const liveEdgeMs = onLiveTrack ? live.offsetMs : track.durationMs;

  return (
    <div className="ir-page" style={{ "--ir-accent": (track.palette || [])[0] || "#3b4a6b" }}>
      <div className="ir-backdrop" style={backdropStyle(track)} />
      <Visualizer bpm={track.bpm} getPositionMs={getPositionMs} palette={track.palette} paused={!playing} />

      <div className="ir-scrim" />

      <header className="ir-topbar">
        <a className="ir-brand joystix" href="/">Clozemaster</a>
        <div className="ir-topbar-right">
          {stations.length > 1 ? (
            <select
              aria-label="Tune to another station"
              className="ir-tuner"
              onChange={(event) => { window.location.href = event.target.value; }}
              value={currentStationUrl}
            >
              {stations.map((option) => (
                <option key={option.url} value={option.url}>
                  {option.language} {option.cefrLevel}
                </option>
              ))}
            </select>
          ) : (
            <span className="ir-station">{station.language} &middot; {station.cefrLevel}</span>
          )}
          {/* The labs thumbs up. This page draws its own topbar, so it's here rather than in the view. */}
          {vote && <VoteButton {...vote} variant="dark" />}
          {labsUrl && (
            <a className="ir-topbar-btn" href={labsUrl}>
              All labs
            </a>
          )}
          {feedbackUrl && (
            <button className="ir-topbar-btn" onClick={() => setFeedbackOpen(true)} type="button">
              Feedback
            </button>
          )}
        </div>
      </header>

      <main className="ir-stage">
        {tunedIn ? (
          <Lyrics
            activeIndex={active}
            lines={lines}
            mode={mode}
            onAdd={openAdd}
            onPlayFrom={playFrom}
            positionMs={positionMs}
            showEnglish={showEnglish}
            trackId={track.id}
          />
        ) : (
          <div className="ir-tunein">
            <h1 className="ir-tunein-title">Infinite Radio</h1>
            <p className="ir-tunein-blurb">
              <span>An endless AI-generated radio station</span>
              <span>where every lyric is written at {station.cefrLevel} {station.language}.</span>
            </p>
            <button className="ir-tunein-btn" onClick={tuneIn} type="button">
              {deepLinkIndex >= 0 ? "Play this song" : "Tune in"}
            </button>
            {/* Block spans rather than a wrapped paragraph, so the two lines stay
                two at every width. */}
            <p className="ir-tunein-lines">
              <span>Read along as it plays.</span>
              <span>Save any line you want to practise.</span>
            </p>
          </div>
        )}
      </main>

      <footer className="ir-bar">
        {/* Scrub within the current song. The range caps at the live edge rather
            than the track length, so the thumb can't be dragged into audio that
            hasn't aired - you can fall behind the broadcast and catch back up,
            but never get ahead of it. */}
        <div className="ir-progress">
          <span className="ir-progress-time">{formatTime(positionMs)}</span>

          <div className="ir-progress-track">
            <input
              aria-label="Seek within this song"
              className="ir-progress-input"
              disabled={!tunedIn}
              max={track.durationMs}
              min={0}
              onChange={seek}
              step={250}
              style={{
                "--ir-aired": `${(liveEdgeMs / track.durationMs) * 100}%`,
                "--ir-played": `${(positionMs / track.durationMs) * 100}%`,
              }}
              type="range"
              value={Math.min(positionMs, track.durationMs)}
            />
          </div>

          <span className="ir-progress-time">{formatTime(track.durationMs)}</span>

          <button
            className={`ir-live${behindLive ? " ir-live-behind" : ""}`}
            disabled={!behindLive}
            onClick={syncToClock}
            type="button"
          >
            <span className="ir-live-dot" />
            {behindLive ? "Go live" : "Live"}
          </button>
        </div>

        <div className="ir-bar-now">
          {track.artUrl
            ? <img alt="" className="ir-bar-art" src={track.artUrl} />
            : <span className="ir-bar-art ir-bar-art-empty" style={backdropStyle(track)} />}
          <div className="ir-bar-meta">
            <p className="ir-bar-title">{track.title}</p>
            <p className="ir-bar-genre">{track.genre} &middot; up next: {upNext.title}</p>
          </div>

          <button className="ir-share" onClick={share} title="Copy a link to this song" type="button">
            {copied ? "Copied" : "Share"}
          </button>
        </div>

        <button
          aria-label={playing ? "Pause" : "Play"}
          className="ir-play"
          onClick={tunedIn ? toggle : tuneIn}
          type="button"
        >
          {playing ? <PauseIcon /> : <PlayIcon />}
        </button>

        <div className="ir-modes">
          <div className="ir-mode-group" role="group">
            {MODES.map((option) => (
              <button
                className={`ir-mode${mode === option.key ? " ir-mode-on" : ""}`}
                key={option.key}
                onClick={() => setMode(option.key)}
                type="button"
              >
                {option.label}
              </button>
            ))}
          </div>

          <button
            aria-pressed={showEnglish}
            className={`ir-mode ir-mode-english${showEnglish ? " ir-mode-on" : ""}`}
            disabled={mode === "off"}
            onClick={() => setShowEnglish((on) => !on)}
            type="button"
          >
            English
          </button>
        </div>
      </footer>

      {feedbackOpen && <FeedbackForm feedbackUrl={feedbackUrl} onClose={() => setFeedbackOpen(false)} />}

      {adding && (
        <AddToCollection
          collectionsUrl={collectionsUrl}
          defaultCollectionName={defaultCollectionName}
          isPro={isPro}
          line={adding}
          onClose={closeAdd}
          onSaved={() => track_("line_saved")}
          proUrl={proUrl}
          signInUrl={signInUrl}
          signedIn={signedIn}
        />
      )}

      <audio
        onEnded={onEnded}
        onPause={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
        preload="auto"
        ref={audioRef}
        src={track.audioUrl}
      />
    </div>
  );
}

InfiniteRadio.defaultProps = {
  collectionsUrl: null,
  defaultCollectionName: "My Custom Collection",
  feedbackUrl: null,
  isPro: false,
  labsUrl: null,
  proUrl: "/pro",
  signInUrl: "/users/sign_up",
  signedIn: false,
  stations: [],
  vote: null,
};

InfiniteRadio.propTypes = {
  collectionsUrl: PropTypes.string,
  defaultCollectionName: PropTypes.string,
  feedbackUrl: PropTypes.string,
  isPro: PropTypes.bool,
  labsUrl: PropTypes.string,
  proUrl: PropTypes.string,
  signInUrl: PropTypes.string,
  signedIn: PropTypes.bool,
  station: PropTypes.shape({
    cefrLevel: PropTypes.string.isRequired,
    language: PropTypes.string.isRequired,
    languageCode: PropTypes.string,
  }).isRequired,
  stations: PropTypes.arrayOf(PropTypes.object),
  tracks: PropTypes.arrayOf(PropTypes.object).isRequired,
  vote: PropTypes.object,
};
